<template>
  <v-container max-width="1200" class="pa-4">
    <v-btn color="primary" @click="showAddSection = true">Add Section</v-btn>
    <v-btn color="primary" @click="showPreview = true">Preview</v-btn>
    <v-divider></v-divider>

    <draggable
      v-model="sections"
      item-key="order"
      :group="{ name: 'sections', pull: 'clone', put: false }"
      :animation="200"
      :ghostClass="'ghost'"
      :forceFallback="true"
      filter=".ignore-drag"
      @end="onDragEnd">
      <template #item="{ element }">
        <v-card>
          <v-card-title>
            <v-row>
              <v-col cols="12" md="1">
                <v-icon class="cursor-move">mdi-drag</v-icon>
              </v-col>
              <v-col cols="12" md="10" class="text-h6">
                {{ element.name }}
              </v-col>
              <v-col cols="12" md="1">
                <div class="ignore-drag ml-auto jus">
                  <v-icon @click="showModal(element)">mdi-pencil</v-icon>
                  <v-icon @click="openDeleteModal(element)">mdi-trash-can-outline</v-icon>
                </div>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text class="ignore-drag">
            <EntrySectionComponent
              :section="element"
              :year-id="yearId"
              :entry-id="entry.id"
              @snack="throwSnack">
            </EntrySectionComponent>
          </v-card-text>
        </v-card>
      </template>
    </draggable>
    <v-dialog :model-value="showPreview">
      <EntryFormPreviewSubView :year="year" @close="showPreview = false"></EntryFormPreviewSubView>
    </v-dialog>

    <EntryAddSectionComponent
      :entry="entry"
      :year-id="yearId"
      :show="showAddSection"
      :is-add="isAdd"
      :section="selectedSection"
      @snack="throwSnack"
      @entrySectionSaved="entrySectionSaved"
      @entrySectionCancled="showAddSection = false"></EntryAddSectionComponent>
    <ConfirmDeleteComponent
      :isOpen="showDelete"
      item="Section"
      :description="selectedSection?.name || ''"
      @actionConfirmed="handleDeleteSection"
      @actionCanceled="showDelete = false"></ConfirmDeleteComponent>
  </v-container>
</template>
<script setup lang="ts">
import type { SortableEvent } from 'sortablejs';
import EntryAddSectionComponent from '@/components/entry/EntryAddSectionComponent.vue';
import EntrySectionComponent from '@/components/entry/EntrySectionComponent.vue';
import draggable from 'vuedraggable';
import { EntryDTO, EntrySectionDTO, IEntrySectionDTO, IYearDTO } from '@/dtos';
import { useSnack } from '@/helpers';
import { useEntryStore, useUpdateStatusStore } from '@/stores';
import { SNACKSTATUS } from '@/stores/util';
import { computed, ref, watch } from 'vue';
import ConfirmDeleteComponent from '@/components/ui/ConfirmDeleteComponent.vue';
import EntryFormPreviewSubView from './EntryFormPreviewSubView.vue';
const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();
const { throwSnack } = useSnack(emit);
const props = defineProps<{ year: IYearDTO }>();
const year = computed(() => props.year);
const yearId = computed(() => year.value.id);

const entryStore = useEntryStore();
const statusStore = useUpdateStatusStore();
const { data } = entryStore.getEntryByYearQuery(yearId);
const entry = computed(() => data.value ?? new EntryDTO());

const sections = ref<IEntrySectionDTO[]>([]);
const showAddSection = ref<boolean>(false);
const isAdd = ref<boolean>(true);
const selectedSection = ref<EntrySectionDTO | undefined>(undefined);
const showDelete = ref<boolean>(false);
const showPreview = ref<boolean>(false);

watch(
  () => entry.value.sections,
  (value: IEntrySectionDTO[]) => {
    sections.value = value.map((section) => new EntrySectionDTO(section));
  },
  { immediate: true },
);

const showModal = (element?: IEntrySectionDTO) => {
  if (element && element.id) {
    isAdd.value = false;
    selectedSection.value = new EntrySectionDTO(element);
  } else {
    isAdd.value = true;
    selectedSection.value = undefined;
  }
  showAddSection.value = true;
};

const openDeleteModal = (field: IEntrySectionDTO) => {
  selectedSection.value = new EntrySectionDTO(field);
  showDelete.value = true;
};

const entrySectionSaved = () => {
  showAddSection.value = false;
};

function onDragEnd(evt: SortableEvent) {
  if (evt.oldIndex === evt.newIndex) {
    return;
  }

  sections.value.forEach((section, index) => {
    section.order = index + 1;
  });
  entryStore.updateSections.mutateAsync({
    entryId: entry.value.id,
    sections: sections.value,
    yearId: yearId.value,
  });
}
const key = computed(() => `entry-section-update-${entry.value.id}`);
watch(
  () => statusStore.getStatus(key.value),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    emit('snack', 'Sections', value, statusStore.getError(key.value));
  },
);

const handleDeleteSection = () => {
  if (!selectedSection.value || !selectedSection.value.id) {
    return;
  }
  entryStore.removeSection.mutateAsync({
    section: selectedSection.value,
    yearId: yearId.value,
  });
  showDelete.value = false;
};
</script>

<style scoped>
.ghost {
  opacity: 0.5;
  background-color: #f0f0f0;
  border: 1px dashed #ccc;
}
</style>
